import {
  ClockIcon,
  CloseIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  Typography,
} from '@acceso/design-system';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FooterFormActions } from '@/components/FooterFormActions/FooterFormActions';
import { InputArea } from '@/components/FormFields/InputArea';
import { InputDate } from '@/components/FormFields/InputDate';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { TSegmentOcrForm } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitRotulo/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePatchClipOcr } from '@/features/clips/api/patch-clip-ocr';
import { usePostClipOcr } from '@/features/clips/api/post-clip-ocr';
import { TPostClipOcr } from '@/features/clips/types';
import { getAbsoluteDateFromPlayerTime } from '@/utils/hh-mm-ss-into-number';

import { ROTULO_FORM_DEFAULT_VALUES } from '../constants';

export function ClipRotuloForm() {
  const {
    activeClip,
    rotuloClipData,
    playerVideoCurrentTime,
    formBox,
    setRotuloClipData,
    setFormBox,
    drawnFormReset,
    setActiveClip,
    setClipItems,
    handleDateTimeClip,
  } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const postClipOcr = usePostClipOcr();
  const patchClipOcr = usePatchClipOcr();

  const form = useForm<TSegmentOcrForm>({
    mode: 'all',
    defaultValues: ROTULO_FORM_DEFAULT_VALUES,
  });

  const handleSubmit = async (data: TSegmentOcrForm) => {
    const formatedData: TPostClipOcr = {
      ...data,
      clip_id: activeClip?.id || '',
    };
    if (rotuloClipData?.id) {
      handleUpdate(formatedData);
    } else {
      handleCreate(formatedData);
    }
  };

  const handleCreate = async (formatedData: TPostClipOcr) => {
    try {
      const item = await postClipOcr.mutateAsync(formatedData);
      setActiveClip(prev => {
        if (prev) {
          return {
            ...prev,
            clip_ocrs: [...(prev.clip_ocrs || []), item],
          };
        }
        return prev;
      });
      setClipItems(prev =>
        prev.map(clip => {
          if (clip.id === formatedData?.clip_id) {
            return { ...clip, clip_ocrs: [...(clip.clip_ocrs || []), item] };
          }
          return clip;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al crear el logo:', error);
    }
  };

  const handleUpdate = async (formatedData: TPostClipOcr) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { clip_id, ...restData } = formatedData;
    try {
      const item = await patchClipOcr.mutateAsync({
        id: rotuloClipData?.id || '',
        body: restData,
      });
      setActiveClip(prev => {
        if (prev) {
          return {
            ...prev,
            clip_ocrs: prev.clip_ocrs?.map(logo => {
              if (logo.id === item.id) {
                return item;
              }
              return logo;
            }),
          };
        }
        return prev;
      });
      setClipItems(prev =>
        prev.map(clip => {
          if (clip.id === formatedData?.clip_id) {
            return {
              ...clip,
              clip_ocrs: (clip.clip_ocrs || []).map(ocr => {
                if (ocr.id === item.id) {
                  return item;
                }
                return ocr;
              }),
            };
          }
          return clip;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al actualizar el logo:', error);
    }
  };

  const handleClickExit = useCallback(() => {
    form.reset(ROTULO_FORM_DEFAULT_VALUES);
    setRotuloClipData(null);
    drawnFormReset();
  }, [setFormBox, setRotuloClipData]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClickExit();
      }

      if (event.ctrlKey && event.key === 'F2') {
        event.preventDefault();

        if (!activeClip?.start_datetime) return;

        const newDateTime = getAbsoluteDateFromPlayerTime(
          activeClip.start_datetime,
          playerVideoCurrentTime
        );

        form.setValue('start_datetime', newDateTime);
      }

      if (event.ctrlKey && event.key === 'F9') {
        event.preventDefault();

        if (!activeClip?.start_datetime) return;

        const newDateTime = getAbsoluteDateFromPlayerTime(
          activeClip.start_datetime,
          playerVideoCurrentTime
        );

        form.setValue('end_datetime', newDateTime);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeClip, form]);

  useEffect(() => {
    if (formBox !== 'rotulo') return;

    if (rotuloClipData?.id) {
      form.setValue('start_datetime', new Date(rotuloClipData.start_datetime));
      form.setValue(
        'end_datetime',
        new Date(rotuloClipData.end_datetime || rotuloClipData.start_datetime)
      );
      /* form.setValue('review_state', rotuloClipData.review_state); */
      form.setValue('text', rotuloClipData.text || '');
    }
  }, [rotuloClipData]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between border-b-[1px] border-accent-disabled-10">
        <Typography
          fontSize={FontSizeEnum.Xl}
          color={FontColorEnum.Dark10}
          weight={FontWeightEnum.Medium}
        >
          {`${rotuloClipData ? t('Editar rótulo') : t('Crear nuevo rótulo')}`}
        </Typography>

        <IconButton
          size={IconButtonSizeEnum.LG}
          color={IconButtonColorEnum.NONE_DEFAULT}
          onClick={handleClickExit}
        >
          <CloseIcon size={IconSizeEnum.LG} />
        </IconButton>
      </div>
      <InputArea form={form} name="text" required label={t('Rótulo')} bigSize />
      <RowContainer>
        <InputDate
          form={form}
          name="start_datetime"
          required
          label={t('Inicio')}
          seconds
          help
          tooltipHelp="Rellenar"
          tooltipKeyword="F2"
          tooltipModifiers={['Ctrl']}
          iconTooltip={ClockIcon}
          onClickTooltip={() => handleDateTimeClip(form, 'start_datetime')}
        />
        <InputDate
          form={form}
          name="end_datetime"
          required
          label={t('Fin')}
          seconds
          help
          tooltipHelp="Rellenar"
          tooltipKeyword="F9"
          tooltipModifiers={['Ctrl']}
          iconTooltip={ClockIcon}
          onClickTooltip={() => handleDateTimeClip(form, 'end_datetime')}
        />
      </RowContainer>

      {/* <StateForm
        className="!justify-start"
        form={form}
        name="review_state"
        activedName="Marcar como revisado"
        desactivedName="No revisado"
      /> */}

      <FooterFormActions form={form} handleCancel={handleClickExit} handleSubmit={handleSubmit} />
    </div>
  );
}
